import { api } from "@/api/client";

// One reviewer's activity on one day (Central time). See ActivityQuery.
export interface ActivityRow {
  date: string;
  user_id: number | null;
  bills_posted: number;
  credits_posted: number;
  lines_posted: number;
  fields_edited: number;
  lines_added: number;
  lines_removed: number;
  lines_changed: number;
  auto_filled_fields: number;
  auto_filled_kept: number;
  pct_auto_filled_kept: number | null;
  median_minutes_to_post: number | null;
  duplicate_overrides: number;
  // Edits estimated from ai_output for drafts that predate the revision log.
  estimated: boolean;
}

export interface ActivityReport {
  from: string;
  to: string;
  time_zone: string;
  rows: ActivityRow[];
  totals: Omit<ActivityRow, "date" | "user_id">;
  by_vendor: Array<{
    netsuite_vendor_id: number | null; vendor_name: string | null; bills: number;
    fields_changed: number; fields_changed_per_bill: number; estimated: boolean;
  }>;
  users: Record<string, { id: number; name: string | null; email: string }>;
}

// Bills and credits created in NetSuite without going through the app (no vbd_
// externalId), per Central-time day and per creator. from_inbox = has a
// SquareWorks invoice document (came through ap@, so AP triage saw it);
// other_manual = everything else; direct_pay = Lightreach automation, kept out
// of the manual totals. bills/credits count from_inbox + other_manual.
export interface ManualEntryCounts {
  bills: number;
  credits: number;
  from_inbox: number;
  other_manual: number;
  direct_pay: number;
}

export interface ManualEntryReport {
  from: string;
  to: string;
  by_day: Array<ManualEntryCounts & { date: string }>;
  creators: Array<ManualEntryCounts & { creator: string; automated: boolean }>;
  total_bills: number;
  total_credits: number;
  total_from_inbox: number;
  total_other_manual: number;
  total_direct_pay: number;
}

// The inbox backlog panel: Needs review + Ready to bill right now, the pace
// against it, and the dollars likely in it. Fixed windows (30-day pace, 90-day
// bill rate); per day = per business day. See InboxBacklogQuery.
export interface BillRateStats { resolved: number; billed: number; pct: number | null }

export interface InboxBacklogReport {
  generated_at: string;
  time_zone: string;
  backlog: {
    total: number; needs_review: number; ready_to_bill: number;
    invoices: number; needs_review_invoices: number; ready_to_bill_invoices: number;
    median_age_days: number | null; p90_age_days: number | null; oldest_age_days: number | null;
  };
  pace: {
    window_days: number; business_days: number;
    emails: number; emails_per_day: number;
    arrivals: number; finished: number; arrivals_per_day: number; finished_per_day: number;
    last_week_start: string; last_week_arrivals_per_day: number; last_week_finished_per_day: number;
    // null = not shrinking: people finish no more than arrives.
    days_to_clear: number | null; days_to_clear_last_week: number | null; days_to_clear_no_arrivals: number | null;
  };
  bill_rate: { window_days: number; overall: BillRateStats; needs_review: BillRateStats; ready_to_bill: BillRateStats };
  dollars: {
    known_total: number; known_items: number; largest_known: number | null;
    unknown_invoices: number; unknown_valued: boolean; history_bills: number; history_median: number | null;
    expected: number; p5: number | null; p10: number | null; p50: number | null; p90: number | null; p95: number | null;
    possible_duplicates: { amounts: number; extra_total: number; examples: Array<{ amount: number; decision_ids: number[] }> };
  };
}

export async function fetchActivity(from: string, to: string): Promise<ActivityReport> {
  const { data } = await api.get<ActivityReport>("/admin/activity", { params: { from, to } });
  return data;
}

export async function fetchManualEntry(from: string, to: string): Promise<ManualEntryReport> {
  const { data } = await api.get<ManualEntryReport>("/admin/activity/manual_entry", { params: { from, to } });
  return data;
}

export async function fetchInboxBacklog(): Promise<InboxBacklogReport> {
  const { data } = await api.get<InboxBacklogReport>("/admin/activity/backlog");
  return data;
}

// The inbox counted by email rather than by bill (InboxMessagesQuery).
export type MessageRoute = "quarantined" | "ready_to_bill" | "needs_review" | "no_action" | "not_triaged";
export type MessageResolution = "quarantined" | "filed" | "billed" | "marked_payable" | "not_a_bill";

export interface MessagesDay {
  date: string;
  received: Record<MessageRoute, number>;
  resolved: Record<MessageResolution, number>;
  // Needs review + Ready to bill at the end of the day.
  backlog: number;
}

export interface MessagesReport {
  from: string;
  to: string;
  time_zone: string;
  by_day: MessagesDay[];
  totals: {
    received: Record<MessageRoute, number>; received_total: number;
    resolved: Record<MessageResolution, number>; resolved_total: number;
    backlog_start: number | null; backlog_end: number | null;
    pct_billed_in_app: number | null;
  };
  // Per reviewer: threads first opened in the range, and queue items resolved.
  by_user: Array<{
    user_id: number; opened: number; resolved_total: number;
    resolved: Record<"billed" | "marked_payable" | "not_a_bill", number>;
  }>;
  users: Record<string, { id: number; name: string | null; email: string }>;
  // Into and out of the queues per day over the last four weeks.
  pace: {
    window_days: number; backlog_now: number; into_per_day: number; out_per_day: number;
    days_to_zero: number | null; clears_on: string | null;
  };
}

export async function fetchMessages(from: string, to: string): Promise<MessagesReport> {
  const { data } = await api.get<MessagesReport>("/admin/activity/messages", { params: { from, to } });
  return data;
}
